import type { ReactNode } from 'react';
import { Icons } from "@stacc/flow-ui-components";
import { NormalInfoWrapper } from "../../../tasks/styles";
import { OfferGivenWrapper } from "../../loan/styles";

interface OfferDetailsProps {
  data: any;
  status: string
}
interface Detail {
  label: string
  value: ReactNode
}

function getIndexOfLastRow(details: Detail[]) {
  let lastValidRow = -1;
  let secondLastValidRow = -1;
  let validCount = 0;

  for (let i = 0; i < details.length; i++) {
    if (details[i].value != null) {
      secondLastValidRow = lastValidRow;
      lastValidRow = i;
      validCount++;
    }
  }

  return validCount % 2 === 0
    ? [secondLastValidRow, lastValidRow]
    : [lastValidRow];
}

export const OfferDetailsRow = ({ data, status }: OfferDetailsProps) => {
  const businessStatus = data?.businessStatus;

  // Use offer if available, otherwise fall back to softOffer
  const offerLike = data?.offer ?? data?.softOffer;
  const {
    validUntil,
    insuranceSelected,
    interestRate: baseInterestRate,
    interestRateWithInsurance,
    effectiveInterestRate: baseEffectiveInterestRate,
    effectiveInterestRateWithInsurance,
    monthlyCost: baseMonthlyCost,
    monthlyCostWithInsurance,
    startupFee,
    termFee,
    terms,
    finalLoanAmount,
    insuranceCost
  } = offerLike || {};

  const interestRate = insuranceSelected ? interestRateWithInsurance ?? baseInterestRate : baseInterestRate;
  const effectiveInterestRate = insuranceSelected ? effectiveInterestRateWithInsurance ?? baseEffectiveInterestRate : baseEffectiveInterestRate;
  const monthlyCost = insuranceSelected ? monthlyCostWithInsurance ?? baseMonthlyCost : baseMonthlyCost;

  // Calculate insurance cost if not stored (fallback for older cases)
  // const insuranceCost = storedInsuranceCost ?? (monthlyCost && termFee ? (monthlyCost + termFee) * 0.0879 : null);

  const offerExpirationDate = validUntil;
  const offerExpired = offerExpirationDate ? new Date(offerExpirationDate) < new Date() : false;
  const formattedExpirationDate = offerExpirationDate
    ? Intl.DateTimeFormat("en-GB").format(new Date(offerExpirationDate)).replace(/\//g, ".")
    : null;

  const isDisbursed = businessStatus === "loan-disbursed";
  const offerValidUntilValue = isDisbursed ? "Disbursed" : offerExpired ? "Offer expired" : formattedExpirationDate;
  const offerValidUntilText = isDisbursed ? "Status" : offerExpired ? "Offer status" : "Offer valid until";

  const formattedInterestRate = interestRate ? `${interestRate}%` : null
  const formattedEffectiveInterestRate = effectiveInterestRate ? `${effectiveInterestRate}%` : null

  const offerGivenIcon = finalLoanAmount ? (
    <Icons.SuccessCircle />
  ) : status === "active" ? (
    <Icons.ActiveCircle />
  ) : (
    <Icons.AbortedCircle />
  );

  const offerDetails = [
    { label: offerValidUntilText, value: offerValidUntilValue },
    { label: "Final loan amount", value: finalLoanAmount ? `${new Intl.NumberFormat('es-ES').format(finalLoanAmount)} €` : null },
    { label: "Terms (months)", value: terms },
    { label: "Monthly cost", value: monthlyCost ? `${new Intl.NumberFormat('es-ES').format(monthlyCost)} €` : null },
    { label: "Nominal interest rate", value: formattedInterestRate },
    { label: "Effective interest rate", value: formattedEffectiveInterestRate },
    { label: "Startup fee", value: startupFee ? `${new Intl.NumberFormat('es-ES').format(startupFee)} €` : null },
    { label: "Term fee", value: termFee ? `${new Intl.NumberFormat('es-ES').format(termFee)} €` : null },
    { label: "Insurance selected", value: insuranceSelected ? "Yes" : "No" },
    { label: "Insurance cost", value: insuranceCost ? `${new Intl.NumberFormat('es-ES').format(insuranceCost)} €` : null },
    {
      label: "Offer given", value: <div style={{
        position: "relative",
      }}><OfferGivenWrapper>{offerGivenIcon}</OfferGivenWrapper></div>
    },
  ]

  const lastIndexes = getIndexOfLastRow(offerDetails)

  const isEmpty = offerDetails.every(detail => detail.value === null || detail.value === undefined);
  if (isEmpty) return null

  return (
    <>
      <NormalInfoWrapper $header>Offer Details</NormalInfoWrapper>
      {offerDetails.map((detail, index) => {
        if (detail.value === null || detail.value === undefined) return
        return (
          <NormalInfoWrapper key={`offer-${index}-${detail.label}`} $isLast={lastIndexes.includes(index)}>
            <div>{detail.label}</div>
            <div>{detail.value}</div>
          </NormalInfoWrapper>
        )
      })}
    </>
  )
}